import { MAX_WORDMARK_CHARS } from '@/lib/constants/validators'
import { getDomainSuggestions } from '@/lib/helpers/domains'
import { EventBus, Events } from '../lib/events'
import { useLocalStorage } from '@/lib/helpers/storage'
import { flat } from '@/lib/helpers/flat'

export const state = () => ({
  characterLimit: MAX_WORDMARK_CHARS,
  allowConcatenation: false,
  domainAvailable: false,
  allowUppercase: false,
  allowSynonyms: false,
  placement: 'any',
  wordLimit: 5,
  quantity: 6,
  query: null,
  names: [],
  logos: [],
  tlds: [],
  nameStyles: [],
  previousSearch: {},
  renderedLogos: [],
  suggestionNumber: 7,
  FAVOURITES_ITEM: 'bng-favourite-names',
  favourites: [],
})

export const mutations = {
  /*
   * Generic mutation to set a State property by it's key
   */
  setProperty(state, { name, value }) {
    state[name] = value
  },

  /*
   * Insert some more Business Names into State.
   */
  addNames(state, names) {
    state.names.push(...names)
  },

  /*
   * Insert some more Logos into State.
   */
  addLogos(state, logos) {
    state.logos.push(...logos)
  },

  /*
   * Clear logos from the state.
   */
  clearLogos(state) {
    state.logos = []
  },

  /*
   * Empty our generated names & logos 👋🏻
   */
  reset(state) {
    state.names = []
    state.logos = []
    EventBus.$emit(Events.BNGClearNames)
  },

  addRenderedLogo(state, logo) {
    state.renderedLogos.push(logo)
  },

  addFavourite(state, name) {
    if (state.favourites.some(favName => favName.value === name.value)) return
    state.favourites.push(name)

    useLocalStorage().setItem(
      state.FAVOURITES_ITEM,
      JSON.stringify(state.favourites)
    )
  },

  removeFavourite(state, name) {
    state.favourites = state.favourites.filter(
      favName => favName.value !== name.value
    )

    useLocalStorage().setItem(
      state.FAVOURITES_ITEM,
      JSON.stringify(state.favourites)
    )
  },
}

export const getters = {
  /*
   * De-dupe & combine our names/logos.
   * Used to display generated names
   * while waiting for the logo to load in.
   */
  results({ names, logos }) {
    return names.reduce((all, name) => {
      // Only keep this name if we don't already have it!
      if (!all.find(({ value }) => value === name.value)) {
        const logo = logos.find(({ wordmark }) => wordmark === name.value)
        all.push({ ...name, logo })
      }

      return all
    }, [])
  },

  getSuggestionNumber({ suggestionNumber }) {
    return suggestionNumber
  },
}

export const actions = {
  /**
   * Generates business names from the user input & fills the
   * `business-name-generator/names` Store.
   * Called and try/catched within `this.generate()`
   */
  async generateNames({ commit, state, dispatch }) {
    // Sort our domains so when we compare searches it should match
    const tlds = [...state.tlds].sort()

    const params = {
      allow_concatenation: state.allowConcatenation,
      has_available_domain: state.domainAvailable,
      name_max_length: state.characterLimit,
      allow_uppercase: state.allowUppercase,
      allow_synonyms: state.allowSynonyms,
      keyword_placement: state.placement,
      word_limit: state.wordLimit,
      query: state.query,
      quantity: state.quantity,
      tlds,
    }

    // If our current search is different from the last one, reset our list
    // of results
    if (
      state.previousSearch !== {} &&
      JSON.stringify(params) !== JSON.stringify(state.previousSearch)
    ) {
      commit('reset')
    }

    // Generate the names
    const {
      data: { names },
    } = await this.$api.get(process.env.BUSINESS_NAME_API, {
      params,
    })

    // Overwrite domains
    const updated_names = await Promise.all(
      names.map(async name => {
        const domains = await dispatch('generateDomains', {
          name: name.value,
          quantity: state.suggestionNumber,
        })
        const trimmedName = name.value
          .replace(/[^a-zA-Z0-9-]/g, '')
          .toLowerCase()
        const formattedName = `${trimmedName}.`
        const has_available_domain = domains.some(domain => domain.available)
        const exact_domain = domains.find(domain =>
          domain.domain.startsWith(formattedName)
        )
        return {
          ...name,
          has_available_domain,
          exact_domain:
            exact_domain && exact_domain.available ? exact_domain.domain : null,
          domains,
        }
      })
    )

    const final_names = updated_names

    // Save the names to state
    commit('addNames', final_names)

    // Save the search to compare
    commit('setProperty', { name: 'previousSearch', value: params })

    return final_names
  },

  async generateSimilarNames(
    { state },
    { name, quantity = state.suggestionNumber }
  ) {
    const params = {
      allow_concatenation: state.allowConcatenation,
      has_available_domain: state.domainAvailable,
      name_max_length: state.characterLimit,
      allow_uppercase: state.allowUppercase,
      allow_synonyms: state.allowSynonyms,
      keyword_placement: state.placement,
      word_limit: state.wordLimit,
      query: name,
      quantity,
    }

    // Generate the names
    const {
      data: { names },
    } = await this.$api.get(process.env.BUSINESS_NAME_API, {
      params,
    })

    return names
  },

  async generateLogo({ commit }, { name, svgroot, canvasdom, logoBuilder }) {
    const logos = await logoBuilder.getLogoIdeas({
      amount: 1,
      brand: {
        wordmark: name,
        slogan: '',
      },
      svgjs: svgroot,
      canvasdom,
    })
    const logo = logos[0]
    commit('addRenderedLogo', logo)
    return logo
  },

  /**
   * Generates domains from a given business name
   */
  async generateDomains({ state, dispatch }, { name, quantity }) {
    // Limit to letters, numbers, and hyphens
    const trimmed_name = name.replace(/[^a-zA-Z0-9-]/g, '')
    const combinations = [trimmed_name, ...getDomainSuggestions(trimmed_name)]

    const suggestions = flat(
      combinations.map(combination =>
        state.tlds.map(tld => `${combination}.${tld}`)
      )
    )

    const domains = []
    let availableCount = 0
    // Check suggestions until the desired quantity is met or all are checked
    for (let i = 0; i < suggestions.length; i++) {
      const suggestion = suggestions[i]
      const domain = await dispatch('checkDomain', suggestion)
      domains.push(domain)
      if (domain.available) availableCount++
      if (availableCount >= quantity) break
    }

    // Sort Available domains to before unavailable domains and slice to
    // desired quantity
    return domains.sort((a, b) => b.available - a.available).slice(0, quantity)
  },

  async checkDomain({ dispatch }, domain) {
    const lowercasedDomain = domain.toLowerCase()

    const result = {
      domain: lowercasedDomain,
      available: false,
    }

    // We're also able to check IPs here if needed
    try {
      const ns = await dispatch('getDOHData', {
        domain: lowercasedDomain,
        type: 'NS',
      })
      result.available = ns.length === 0
    } catch (e) {
      console.log(e)
      result.available = false
    }

    return result
  },

  async getDOHData({}, { domain, type }) {
    const result = await fetch(
      `https://cloudflare-dns.com/dns-query?name=${domain}&type=${type}`,
      { headers: { Accept: 'application/dns-json' } }
    ).then(res => res.json())

    if (!result.Answer) {
      return []
    }
    return result.Answer.map(({ data }) => data)
  },

  fetchFavourites({ state, commit }) {
    let favNames = JSON.parse(useLocalStorage().getItem(state.FAVOURITES_ITEM))
    if (!favNames) favNames = []

    commit('setProperty', { name: 'favourites', value: favNames })
  },
}
